import { InformationCircleIcon } from '@heroicons/react/24/outline';
import React from 'react';

import type { Company } from '../../../../@types/Company';
import { isCompanyBrandingComplete } from '../../../../utils/branding';
import { JWTHeader } from '../../../../utils/JWTHeader';
import { processFixedNewsletterContent } from '../../../../utils/newsletterPreview';

interface MiniNewsletterPreviewProps {
  date: string;
  slug: string;
  templateName: string;
  jwt: string | null;
  company: Company | null;
  sizeMode?: 'compact' | 'adaptive';
}

const A4_WIDTH_MM = 210;
const A4_HEIGHT_MM = 297;
const MM_TO_PX = 96 / 25.4;
const FULL_WIDTH_PX = A4_WIDTH_MM * MM_TO_PX;
const FULL_HEIGHT_PX = A4_HEIGHT_MM * MM_TO_PX;
const DEFAULT_PREVIEW_WIDTH_PX = 192;
const DEFAULT_SCALE = DEFAULT_PREVIEW_WIDTH_PX / FULL_WIDTH_PX;
const MIN_SCALE = 0.2;
const PREVIEW_CACHE = new Map<string, string>();

function MiniNewsletterPreview({
  date,
  slug,
  templateName,
  jwt,
  company,
  sizeMode = 'compact',
}: MiniNewsletterPreviewProps) {
  const [processedContent, setProcessedContent] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = React.useState(0);
  const [viewportSize, setViewportSize] = React.useState(() => ({ width: 1280, height: 720 }));

  React.useEffect(() => {
    if (sizeMode !== 'adaptive' || typeof window === 'undefined') {
      return;
    }

    const updateViewportSize = () => {
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    };

    updateViewportSize();
    window.addEventListener('resize', updateViewportSize);

    return () => {
      window.removeEventListener('resize', updateViewportSize);
    };
  }, [sizeMode]);

  const previewSizing = React.useMemo(() => {
    if (sizeMode !== 'adaptive') {
      const width = FULL_WIDTH_PX * DEFAULT_SCALE;
      const height = FULL_HEIGHT_PX * DEFAULT_SCALE;

      return {
        scale: DEFAULT_SCALE,
        width,
        height,
      };
    }

    const { width, height } = viewportSize;
    if (!width || !height) {
      const fallbackWidth = FULL_WIDTH_PX * DEFAULT_SCALE;
      const fallbackHeight = FULL_HEIGHT_PX * DEFAULT_SCALE;

      return {
        scale: DEFAULT_SCALE,
        width: fallbackWidth,
        height: fallbackHeight,
      };
    }

    const isLandscape = width >= height;

    const scaleFromHeight = Math.min((height * 0.65) / FULL_HEIGHT_PX, 1);
    const scaleFromWidth = Math.min((width * 0.75) / FULL_WIDTH_PX, 1);

    const rawScale = isLandscape ? scaleFromHeight : scaleFromWidth;
    const safeScale = Number.isFinite(rawScale) && rawScale > 0 ? Math.max(rawScale, MIN_SCALE) : DEFAULT_SCALE;

    return {
      scale: safeScale,
      width: FULL_WIDTH_PX * safeScale,
      height: FULL_HEIGHT_PX * safeScale,
    };
  }, [sizeMode, viewportSize]);

  const brandingComplete = React.useMemo(() => isCompanyBrandingComplete(company), [company]);
  const brandingSignature = React.useMemo(() => ([
    company?.id ?? 'no-company',
    company?.brandingSettings?.color1 ?? '',
    company?.brandingSettings?.color2 ?? '',
    company?.brandingSettings?.color3 ?? '',
    company?.brandingSettings?.brandingImageName ?? '',
    JSON.stringify(company?.brandingSettings?.referents ?? []),
  ].join('|')), [
    company?.brandingSettings?.color1,
    company?.brandingSettings?.color2,
    company?.brandingSettings?.color3,
    company?.brandingSettings?.brandingImageName,
    company?.brandingSettings?.referents,
    company?.id,
  ]);

  React.useEffect(() => {
    let isActive = true;
    const controller = new AbortController();

    const cacheKey = `${date}:${slug}:${templateName}:${brandingSignature}`;

    const loadPreview = async() => {
      if (!jwt || !company) {
        setError('missing-auth');
        setIsLoading(false);
        return;
      }

      if (PREVIEW_CACHE.has(cacheKey)) {
        setProcessedContent(PREVIEW_CACHE.get(cacheKey) ?? '');
        setCurrentPageIndex(0);
        setError(null);
        setIsLoading(false);
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await fetch(
          `/api/fixed-newsletters/${date}/${slug}`,
          {
            ...JWTHeader(jwt),
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error ${response.status}`);
        }

        const data = await response.json();
        const template = data.templates?.find((item: { name: string }) => item.name === templateName);

        if (!template || !template.content) {
          throw new Error('Template not found');
        }

        const content = await processFixedNewsletterContent(template.content, company, jwt);

        if (!isActive) {
          return;
        }

        PREVIEW_CACHE.set(cacheKey, content);
        setProcessedContent(content);
        setCurrentPageIndex(0);
      } catch (fetchError) {
        if (!isActive || controller.signal.aborted) {
          return;
        }
        console.error('Mini newsletter preview failed:', fetchError);
        setError('load-failed');
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [brandingSignature, company, date, jwt, slug, templateName]);

  const pages = React.useMemo(() => {
    if (!processedContent) {
      return [] as string[];
    }

    try {
      const parser = new DOMParser();
      const doc = parser.parseFromString(`<div>${processedContent}</div>`, 'text/html');
      const root = doc.body.firstElementChild;
      if (!root) {
        return [processedContent];
      }

      const pageNodes = root.querySelectorAll('.newsletter-page');
      if (!pageNodes.length) {
        return [root.innerHTML || processedContent];
      }

      return [...pageNodes].map(page => page.outerHTML);
    } catch (parseError) {
      console.error('Mini newsletter preview: unable to split pages', parseError);
      return [processedContent];
    }
  }, [processedContent]);

  React.useEffect(() => {
    if (currentPageIndex > 0 && currentPageIndex >= pages.length) {
      setCurrentPageIndex(Math.max(pages.length - 1, 0));
    }
  }, [currentPageIndex, pages.length]);

  const totalPages = pages.length;
  const currentPageHtml = totalPages > 0
    ? pages[Math.min(currentPageIndex, totalPages - 1)]
    : '';

  const renderContent = () => {
    if (isLoading) {
      return (
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="h-24 w-20 animate-pulse rounded-md bg-gray-200" />
        </div>
      );
    }

    if (error === 'missing-auth') {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
          <InformationCircleIcon className="h-8 w-8 text-gray-400" />
          <p className="text-xs text-gray-500">
            Identifiez-vous pour prévisualiser cette newsletter.
          </p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 p-4 text-center">
          <InformationCircleIcon className="h-8 w-8 text-red-400" />
          <p className="text-xs text-red-500">
            Impossible de charger l'aperçu.
          </p>
        </div>
      );
    }

    if (!currentPageHtml) {
      return null;
    }

    return (
      <div className="absolute inset-0 flex items-start justify-center">
        <div
          className="pointer-events-none"
          style={{ width: '210mm', minHeight: '297mm', transform: `scale(${previewSizing.scale})`, transformOrigin: 'top center' }}
          dangerouslySetInnerHTML={{ __html: currentPageHtml }}
        />
        {!brandingComplete && (
          <div className="pointer-events-auto absolute inset-0 flex items-center justify-center bg-black/40 p-4">
            <div className="rounded-md bg-white/95 p-3 text-center shadow">
              <p className="text-xs font-medium text-gray-700">
                Complétez votre image de marque pour visualiser cette newsletter.
              </p>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div
        className="relative overflow-hidden rounded-lg border border-gray-200 bg-gray-50"
        style={{ width: previewSizing.width, height: previewSizing.height, maxWidth: '100%' }}
        aria-label={'Aperçu miniature de la newsletter'}
      >
        {renderContent()}
      </div>
      {brandingComplete && totalPages > 1 && (
        <div className="flex items-center gap-4 text-xs text-gray-700">
          <button
            type="button"
            onClick={() => setCurrentPageIndex((index) => Math.max(index - 1, 0))}
            className="rounded-full border border-gray-300 px-2 py-1 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={currentPageIndex === 0}
            aria-label="Page précédente"
          >
            &lt;
          </button>
          <span className="font-medium">
            Page {currentPageIndex + 1} / {totalPages}
          </span>
          <button
            type="button"
            onClick={() => setCurrentPageIndex((index) => Math.min(index + 1, totalPages - 1))}
            className="rounded-full border border-gray-300 px-2 py-1 transition hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
            disabled={currentPageIndex >= totalPages - 1}
            aria-label="Page suivante"
          >
            &gt;
          </button>
        </div>
      )}
    </div>
  );
}

export default MiniNewsletterPreview;

